import {
	Instance,
	applySnapshot,
	flow,
	getParent,
	getSnapshot,
	isStateTreeNode,
	types,
} from 'mobx-state-tree';

import {
	addCgCoordinatorsToGroup,
	addStudentsToGroup,
	deleteCgCoordinatorsFromGroup,
	deleteGroup,
	deleteStudentFromGroup,
	getCgCoordinatorsGroup,
	getGroup,
	saveGroup,
} from '@common/api/groups';
import Location from '@common/models/Locations/Location';
import Curator from '@common/models/Users/Curators/Curator';
import Student, { IStudentModel } from '@common/models/Users/Students/Student';

import ServiceTemplate from '../ServiceTemplates/ServiceTemplate';
import TimeZone from '../TimeZone/TimeZone';
import CgCoordinator, { ICgCoordinatorModel } from '../Users/CgCoordinators/CgCoordinator';
import Mentor from '../Users/Mentors/Mentor';
import { storeReference } from '../helpers';
import { IGroupsModel } from './Groups';
import Schedule from './Schedule';

const Group = types
	.model('Group', {
		id: types.identifierNumber,
		name: types.optional(types.string, ''),
		students_count: types.optional(types.number, 0),
		official_name: types.optional(types.string, ''),
		name_en: types.maybeNull(types.optional(types.string, '')),
		// @TODO change to type types.maybe(storeReference(Service))
		service_template: types.maybe(storeReference(ServiceTemplate)),
		students: types.optional(types.array(Student), []),
		cgCoordinators: types.optional(types.array(CgCoordinator), []),
		location: types.maybeNull(Location),
		mentor_id: types.maybeNull(types.number),
		mentor: types.maybeNull(Mentor),
		curator: types.maybeNull(Curator),
		timezone_id: types.maybeNull(types.number),
		timezone: types.maybeNull(TimeZone),
		schedules: types.maybe(types.optional(types.array(Schedule), [])),
		parallel_class_id: types.maybeNull(types.number),
		is_demo: types.maybeNull(types.boolean),
		service_name: types.maybe(types.string),
		group_address_id: types.maybe(types.maybeNull(types.number)),
		isCgService: types.maybeNull(types.boolean),
	})
	.volatile(() => ({
		loading: false,
	}))
	.preProcessSnapshot((snapshot: any) => ({
		...snapshot,
		cgCoordinators:
			snapshot?.cgCoordinators ||
			snapshot?.cgCoorditators ||
			snapshot?.cg_coordinators ||
			snapshot?.c_g_coordinators ||
			[],
	}))
	.views((self) => ({
		get counterUntestedLessons() {
			return self.schedules[0]?.counterUntestedLessons || 0;
		},
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield getGroup(self.id);

				if (self.isCgService) {
					const cgCoordinators = yield getCgCoordinatorsGroup(self.id);
					applySnapshot(self, { ...result, cgCoordinators, isCgService: self.isCgService });
					return;
				}

				applySnapshot(self, { ...result, isCgService: self.isCgService });
			} finally {
				self.loading = false;
			}
		}),
		save: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield saveGroup(self.id, {
					...getSnapshot(self),
					...data,
				});
				applySnapshot(self, { ...result, isCgService: self.isCgService });
			} finally {
				self.loading = false;
			}
		}),
		addStudents: flow(function* (students: IStudentModel[] = []) {
			self.loading = true;
			try {
				yield addStudentsToGroup(self.id, {
					students_ids: students.map((student) => student.id),
				});
				applySnapshot(self.students, students);
			} catch (e) {
				console.error(e);
			} finally {
				self.loading = false;
			}
		}),
		addCgCoordinators: flow(function* (cgCoordinators: ICgCoordinatorModel[] = []) {
			self.loading = true;
			try {
				yield addCgCoordinatorsToGroup(self.id, {
					c_g_coordinator_ids: cgCoordinators.map((coordinator) => coordinator.id),
				});
				applySnapshot(
					self.cgCoordinators,
					cgCoordinators.map((coordinator) =>
						isStateTreeNode(coordinator) ? getSnapshot(coordinator) : coordinator,
					),
				);
			} catch (e) {
				console.error(e);
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			self.loading = true;
			try {
				yield deleteGroup(self.id);
				const parent = getParent<IGroupsModel>(self, 2);
				parent.removeGroup(self as IGroupModel);
			} catch (e) {
				self.loading = false;
			}
		}),
	}))
	.actions((self) => ({
		removeStudent: flow(function* (id: number) {
			self.loading = true;
			try {
				yield deleteStudentFromGroup(self.id, { students_ids: [id] });
				yield self.fetch();
			} finally {
				self.loading = false;
			}
		}),
		removeCoordinator: flow(function* (id: number) {
			self.loading = true;
			try {
				yield deleteCgCoordinatorsFromGroup(self.id, { c_g_coordinator_ids: [id] });
				yield self.fetch();
			} finally {
				self.loading = false;
			}
		}),
	}));

export default Group;

export interface IGroupModel extends Instance<typeof Group> {}
